import React, {PropsWithChildren, useEffect, useMemo, useRef, useState} from 'react';
import {IonPopover} from '@ionic/react';
import {StyledFakeField} from "./input.style";
import {StyledTime, StyledTimeButton, StyledTimeInput} from "@components/form/input/time.style";
import {isValid, parseISO} from "date-fns";
import {Keyboard} from "@capacitor/keyboard";
import {useTranslation} from "react-i18next";

const MomentRange = require('moment-range');
const Moment = require('moment-timezone')
const moment = MomentRange.extendMoment(Moment);
moment.tz.setDefault('Europe/Warsaw');

type TimeProps = {
    value?: string | null;
    disabled?: boolean;
    cancelText?: string;
    doneText?: string;
    placeholder?: string;
    onChange: (value: string) => void;
    className?: string;
    minuteValues?: string;
    keepContentsMounted?: boolean;
    defaultPopoverValue?: string;
}

const TimeInput: React.FC<PropsWithChildren<TimeProps>> = (props) => {
    const {t} = useTranslation();

    const hoursRef = useRef<null | HTMLIonInputElement>(null);
    const minutesRef = useRef<null | HTMLIonInputElement>(null);

    const [selectedValue, setSelectedValue] = useState<string>();
    const [showPopover, setShowPopover] = useState<boolean>(false);
    const [hours, setHours] = useState<string>();
    const [minutes, setMinutes] = useState<string>();
    const [isRounded, setIsRounded] = useState<boolean>(false);

    useEffect(() => {
        if (showPopover) {
            Keyboard.show();

            if (hoursRef.current) {
                focusInputType(hoursRef.current);
            }

            setIsRounded(false);
        }

        if (!showPopover) {
            Keyboard.hide();
        }
    }, [showPopover]);

    useEffect(() => {
        const timeValue = props.value;

        if (timeValue) {
            setSelectedValue(timeValue);
            setHours(moment(timeValue).format("HH"));
            setMinutes(moment(timeValue).format("mm"));
        }
    }, [showPopover, props.value]);

    const formattedValue = useMemo(() => {
        if (props.value && isValid(parseISO(props.value.toString()))) {
            return moment(props.value.toString(), moment.ISO_8601).format('HH:mm');
        }

        return props.placeholder ?? '';
    }, [props.value, props.placeholder]);

    const handleShowPopover = () => {
        if (!props.disabled) {
            setShowPopover(true);
        }
    };

    const handleSave = () => {
        if (!hours || !minutes) {
            return;
        }

        props.onChange(moment(selectedValue).set({hour: hours, minute: minutes}).format());

        setShowPopover(false);
    };

    const handleCancel = () => {
        setHours(undefined);
        setMinutes(undefined);
        setShowPopover(false);
    }

    const handleHoursInput = (e: CustomEvent, isBlur: boolean = false) => {
        const ionInput = e.target as HTMLIonInputElement;
        let hours = replaceNonDigitCharacters(ionInput.value as string ?? '');

        if (hours === '') {
            ionInput.value = '';
            setHours('');
            return;
        }

        hours = replaceToReadableHours(hours, isBlur);

        ionInput.value = hours;
        setHours(hours);

        if (hours.length === 2 && minutesRef.current && !isBlur) {
            focusInputType(minutesRef.current);
        }
    };

    const handleMinutesInput = (e: CustomEvent, isBlur: boolean = false) => {
        const ionInput = e.target as HTMLIonInputElement;
        let minutes = replaceNonDigitCharacters(ionInput.value as string ?? '');

        if (minutes === '') {
            ionInput.value = '';
            setMinutes('');
            return;
        }

        minutes = replaceToReadableMinutes(minutes);

        if (props.minuteValues && (minutes.length === 2 || isBlur)) {
            const closestMinutesValue = getClosestMinutes(parseInt(minutes))

            if (closestMinutesValue && closestMinutesValue !== minutes) {
                minutes = closestMinutesValue;
                setIsRounded(true);
            }
        }

        ionInput.value = minutes;
        setMinutes(minutes);
    };

    const replaceToReadableHours = (hours: string, isBlur: boolean = false) => {
        if (hours.length === 1 && ((parseInt(hours) >= 3 && parseInt(hours) <= 9) || isBlur)) {
            hours = `0${hours}`;
        }

        return hours;
    }

    const replaceToReadableMinutes = (minutes: string) => {
        if (parseInt(minutes) >= 60 && parseInt(minutes) <= 99) {
            minutes = '59';
        }

        return minutes;
    }

    const replaceNonDigitCharacters = (value: string) => {
        return value.replace(/\D/g, '').slice(0, 2);
    }

    const focusInputType = (inputElement: HTMLIonInputElement) => {
        setTimeout(() => {
            inputElement.setFocus();
        }, 150);
    }

    const getClosestMinutes = (minutesValue: number) => {
        if (props.minuteValues) {
            const closestMinutes = props.minuteValues.split(',').reduce((prev, curr) =>
                Math.abs(parseInt(curr) - minutesValue) < Math.abs(parseInt(prev) - minutesValue) ? curr : prev
            );

            return closestMinutes.length === 1 ? `0${closestMinutes}` : closestMinutes;
        }
    };

    return (
        <>
            <StyledFakeField className={props.className} onClick={handleShowPopover}>
                {formattedValue}
            </StyledFakeField>
            <IonPopover
                keepContentsMounted={props.keepContentsMounted}
                isOpen={showPopover}
                onDidDismiss={handleCancel}
            >
                <StyledTime>
                    <StyledTimeInput
                        ref={hoursRef}
                        type="tel"
                        maxlength={2}
                        inputmode="numeric"
                        pattern="[0-9]*"
                        value={hours || (props.defaultPopoverValue ? replaceToReadableHours(moment(props.defaultPopoverValue).hours().toString()) : '' )}
                        onIonInput={handleHoursInput}
                        onIonBlur={(e) => handleHoursInput(e, true)}
                    />
                    :
                    <StyledTimeInput
                        ref={minutesRef}
                        type="tel"
                        maxlength={2}
                        inputmode="numeric"
                        pattern="[0-9]*"
                        value={minutes || (props.defaultPopoverValue ? replaceToReadableMinutes(moment(props.defaultPopoverValue).minutes().toString()) : '')}
                        onIonInput={handleMinutesInput}
                        onIonBlur={(e) => handleMinutesInput(e, true)}
                    />
                </StyledTime>
                {
                    isRounded && <p className="warningMessage" style={{padding: "0 15px"}}>{t('time.minutesHasBeenRounded')}</p>

                }
                <div style={{display: "flex", justifyContent: "space-between"}}>
                    <StyledTimeButton size="small" fill="clear" onClick={handleCancel}>{props.cancelText}</StyledTimeButton>
                    <StyledTimeButton size="small" fill="clear" onClick={handleSave}>{props.doneText}</StyledTimeButton>
                </div>
            </IonPopover>
        </>
    );
}

export default React.memo(TimeInput);